import { ReactNode, useState, createContext, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '../Icon.js';

// ── Confirm context ───────────────────────────────────────────────────────────

type SABConfirmState = { label: string; onConfirm: () => void } | null;
type SABConfirmCtxType = { request: (label: string, cb: () => void) => void } | null;
const SABCtx = createContext<SABConfirmCtxType>(null);

// ── Root ──────────────────────────────────────────────────────────────────────

interface SelectionActionBarProps {
  visible: boolean;
  countLabel: string;
  onClearSelection: () => void;
  children?: ReactNode;
}

function SelectionActionBarRoot({
  visible,
  countLabel,
  onClearSelection,
  children,
}: SelectionActionBarProps) {
  const { t } = useTranslation();
  const [pendingConfirm, setPendingConfirm] = useState<SABConfirmState>(null);

  return (
    <SABCtx.Provider value={{ request: (label, cb) => setPendingConfirm({ label, onConfirm: cb }) }}>
      <div
        style={{
          overflow: 'hidden',
          maxHeight: visible ? '52px' : '0',
          opacity: visible ? 1 : 0,
          transition: 'max-height 150ms ease-out, opacity 150ms ease-out',
          flexShrink: 0,
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            height: '52px',
            padding: '0 var(--evo-content-pad-x)',
            background: 'var(--evo-panel-strong)',
            borderTop: '1px solid var(--evo-stroke)',
            borderBottom: '1px solid var(--evo-stroke)',
          }}
        >
          {/* Count label */}
          <span
            style={{
              fontFamily: 'var(--evo-font)',
              fontSize: '13px',
              fontWeight: 600,
              color: 'var(--evo-cyan)',
              flexShrink: 0,
              whiteSpace: 'nowrap',
            }}
          >
            {countLabel}
          </span>

          {/* Divider */}
          <div
            style={{
              width: '1px',
              height: '16px',
              background: 'var(--evo-stroke)',
              flexShrink: 0,
              margin: '0 4px',
            }}
          />

          {/* Actions or confirm overlay */}
          {pendingConfirm ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1, minWidth: 0 }}>
              <span style={{
                fontFamily: 'var(--evo-font)',
                fontSize: '12px',
                color: 'var(--evo-danger)',
                flexShrink: 0,
                whiteSpace: 'nowrap',
              }}>
                {t('common.areYouSure')}
              </span>
              <SABBtn label={t('common.cancel')} tone="default" onClick={() => setPendingConfirm(null)} />
              <SABBtn
                label={pendingConfirm.label}
                tone="danger-fill"
                onClick={() => { pendingConfirm.onConfirm(); setPendingConfirm(null); }}
              />
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1, flexWrap: 'wrap', minWidth: 0 }}>
              {children}
            </div>
          )}

          {/* Clear (×) */}
          <button
            type="button"
            onClick={() => { setPendingConfirm(null); onClearSelection(); }}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '24px',
              height: '24px',
              borderRadius: '4px',
              border: '1px solid var(--evo-stroke)',
              background: 'transparent',
              color: 'var(--evo-fg-3)',
              cursor: 'pointer',
              flexShrink: 0,
              transition: 'opacity 120ms',
            }}
          >
            <Icon name="close" size={12} />
          </button>
        </div>
      </div>
    </SABCtx.Provider>
  );
}

// ── Palette ───────────────────────────────────────────────────────────────────

type SABTone = 'default' | 'primary' | 'danger' | 'danger-fill' | 'amber';

const SAB_PALETTE: Record<SABTone, { border: string; color: string; bg: string }> = {
  'default':     { border: 'var(--evo-stroke)',  color: 'var(--evo-fg-2)',   bg: 'transparent'        },
  'primary':     { border: 'var(--evo-cyan)',     color: '#0a0a0a',           bg: 'var(--evo-cyan)'    },
  'danger':      { border: 'color-mix(in srgb, var(--evo-danger) 40%, transparent)', color: 'var(--evo-danger)', bg: 'transparent' },
  'danger-fill': { border: 'var(--evo-danger)',   color: 'var(--evo-fg-1)',   bg: 'var(--evo-danger)'  },
  'amber':       { border: 'var(--evo-amber)',    color: 'var(--evo-amber)',  bg: 'transparent'        },
};

// ── Internal button (no context, used by confirm overlay) ─────────────────────

interface SABBtnProps {
  label: string;
  tone?: SABTone;
  onClick?: () => void;
  loading?: boolean;
  disabled?: boolean;
}

function SABBtn({ label, tone = 'default', onClick, loading, disabled }: SABBtnProps) {
  const c = SAB_PALETTE[tone];
  const isDisabled = disabled || loading;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={isDisabled}
      style={{
        height: '28px',
        padding: '0 12px',
        borderRadius: '4px',
        border: `1px solid ${c.border}`,
        background: c.bg,
        color: c.color,
        fontFamily: 'var(--evo-font)',
        fontSize: '12px',
        fontWeight: 500,
        cursor: isDisabled ? 'not-allowed' : 'pointer',
        opacity: isDisabled ? 0.5 : 1,
        transition: 'opacity 120ms',
        flexShrink: 0,
        whiteSpace: 'nowrap',
      }}
    >
      {loading ? '…' : label}
    </button>
  );
}

// ── Public Action (context-aware, supports confirmRequired) ───────────────────

interface SABActionProps extends SABBtnProps {
  confirmRequired?: boolean;
}

function SABAction({ confirmRequired, onClick, ...rest }: SABActionProps) {
  const ctx = useContext(SABCtx);

  function handleClick() {
    if (confirmRequired && ctx) {
      ctx.request(rest.label, () => onClick?.());
    } else {
      onClick?.();
    }
  }

  return <SABBtn {...rest} onClick={handleClick} />;
}

// ── Divider ───────────────────────────────────────────────────────────────────

function Divider() {
  return (
    <div
      style={{
        width: 1,
        height: 20,
        background: 'var(--evo-fg-3)',
        margin: '0 8px',
        flexShrink: 0,
      }}
    />
  );
}

// ── Export ────────────────────────────────────────────────────────────────────

export const SelectionActionBar = Object.assign(SelectionActionBarRoot, { Action: SABAction, Divider });
